// SNIGJ - Trial/Process Tracking Page
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { 
  Scale, Users, FileText, Clock, Calendar, MapPin, Plus, Eye, Edit, Save,
  CheckCircle, AlertTriangle, User, Briefcase, Shield, Gavel, ScrollText,
  Camera, Video, Mic, File, Link2, ChevronRight, History, Lock, Unlock
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

// Trial Stages
const TRIAL_STAGES = [
  { id: 'ENQUETE_PRELIMINAIRE', name: { fr: 'Enquête préliminaire', en: 'Preliminary Investigation' }, level: 'OPJ', icon: Shield },
  { id: 'INSTRUCTION', name: { fr: 'Instruction', en: 'Judicial Investigation' }, level: 'MAGISTRATE', icon: ScrollText },
  { id: 'RENVOI', name: { fr: 'Renvoi/Mise en accusation', en: 'Indictment' }, level: 'MAGISTRATE', icon: Gavel },
  { id: 'AUDIENCE_PREMIERE_INSTANCE', name: { fr: 'Audience 1ère instance', en: 'First Instance Hearing' }, level: 'COURT', icon: Scale },
  { id: 'DELIBERE', name: { fr: 'Délibéré', en: 'Deliberation' }, level: 'COURT', icon: Users },
  { id: 'JUGEMENT', name: { fr: 'Jugement', en: 'Judgment' }, level: 'COURT', icon: Gavel },
  { id: 'APPEL', name: { fr: 'Appel', en: 'Appeal' }, level: 'APPEAL_COURT', icon: Scale },
  { id: 'ARRET_APPEL', name: { fr: "Arrêt Cour d'Appel", en: 'Appeal Court Ruling' }, level: 'APPEAL_COURT', icon: Gavel },
  { id: 'CONSEIL_ETAT', name: { fr: "Conseil d'État", en: 'Council of State' }, level: 'COUNCIL', icon: Scale },
  { id: 'CASSATION', name: { fr: 'Pourvoi en cassation', en: 'Supreme Court Appeal' }, level: 'SUPREME_COURT', icon: Scale },
  { id: 'ARRET_CASSATION', name: { fr: 'Arrêt Cour de Cassation', en: 'Supreme Court Ruling' }, level: 'SUPREME_COURT', icon: Gavel }
];

// Actor Roles
const ACTOR_ROLES = [
  { id: 'OPJ', name: { fr: 'Officier de Police Judiciaire', en: 'Judicial Police Officer' }, icon: Shield },
  { id: 'MAGISTRAT_INSTRUCTEUR', name: { fr: 'Magistrat instructeur', en: 'Investigating Magistrate' }, icon: ScrollText },
  { id: 'PROCUREUR', name: { fr: 'Procureur / Ministère Public', en: 'Prosecutor' }, icon: Briefcase },
  { id: 'JUGE', name: { fr: 'Juge', en: 'Judge' }, icon: Gavel },
  { id: 'PRESIDENT_TRIBUNAL', name: { fr: 'Président du tribunal', en: 'Presiding Judge' }, icon: Gavel },
  { id: 'GREFFIER', name: { fr: 'Greffier', en: 'Clerk' }, icon: FileText },
  { id: 'AVOCAT_DEFENSE', name: { fr: 'Avocat de la défense', en: 'Defense Attorney' }, icon: Briefcase },
  { id: 'AVOCAT_PARTIE_CIVILE', name: { fr: 'Avocat partie civile', en: 'Civil Party Attorney' }, icon: Briefcase },
  { id: 'TEMOIN', name: { fr: 'Témoin', en: 'Witness' }, icon: User },
  { id: 'EXPERT', name: { fr: 'Expert', en: 'Expert' }, icon: User },
  { id: 'ACCUSE', name: { fr: 'Accusé / Prévenu', en: 'Defendant' }, icon: User },
  { id: 'PARTIE_CIVILE', name: { fr: 'Partie civile (Victime)', en: 'Civil Party (Victim)' }, icon: User }
];

// Evidence Types
const EVIDENCE_TYPES = [
  { id: 'PV_AUDITION', name: { fr: "Procès-verbal d'audition", en: 'Hearing Report' }, icon: FileText },
  { id: 'PV_CONSTAT', name: { fr: 'Procès-verbal de constat', en: 'Observation Report' }, icon: FileText },
  { id: 'RAPPORT_EXPERTISE', name: { fr: "Rapport d'expertise", en: 'Expert Report' }, icon: FileText },
  { id: 'PIECE_CONVICTION', name: { fr: 'Pièce à conviction', en: 'Exhibit' }, icon: File },
  { id: 'TEMOIGNAGE', name: { fr: 'Témoignage', en: 'Testimony' }, icon: Mic },
  { id: 'PHOTO', name: { fr: 'Photo', en: 'Photo' }, icon: Camera },
  { id: 'VIDEO', name: { fr: 'Vidéo', en: 'Video' }, icon: Video },
  { id: 'RAPPORT_MEDICO_LEGAL', name: { fr: 'Rapport médico-légal', en: 'Forensic Report' }, icon: FileText },
  { id: 'RAPPORT_BALISTIQUE', name: { fr: 'Rapport balistique', en: 'Ballistic Report' }, icon: FileText },
  { id: 'RAPPORT_TOXICOLOGIQUE', name: { fr: 'Rapport toxicologique', en: 'Toxicology Report' }, icon: FileText },
  { id: 'DOCUMENT_BANCAIRE', name: { fr: 'Document bancaire', en: 'Bank Document' }, icon: FileText },
  { id: 'AUTRE', name: { fr: 'Autre', en: 'Other' }, icon: File }
];

// Document Types
const JUDICIAL_DOCUMENTS = [
  { id: 'MANDAT_ARRET', name: { fr: "Mandat d'arrêt", en: 'Arrest Warrant' } },
  { id: 'MANDAT_PERQUISITION', name: { fr: 'Mandat de perquisition', en: 'Search Warrant' } },
  { id: 'MANDAT_COMPARUTION', name: { fr: 'Mandat de comparution', en: 'Summons' } },
  { id: 'REQUISITOIRE', name: { fr: 'Réquisitoire', en: 'Indictment Request' } },
  { id: 'ORDONNANCE', name: { fr: 'Ordonnance', en: 'Court Order' } },
  { id: 'JUGEMENT', name: { fr: 'Jugement', en: 'Judgment' } },
  { id: 'ARRET', name: { fr: 'Arrêt', en: 'Ruling' } },
  { id: 'NOTIFICATION', name: { fr: 'Notification', en: 'Notification' } },
  { id: 'ACTE_APPEL', name: { fr: "Acte d'appel", en: 'Appeal Notice' } },
  { id: 'MEMOIRE', name: { fr: 'Mémoire', en: 'Brief' } }
];

const TrialTrackingPage = () => {
  const { language, user } = useAuth();
  const navigate = useNavigate();
  const { caseId } = useParams();
  const [loading, setLoading] = useState(true);
  const [caseData, setCaseData] = useState(null);
  const [trial, setTrial] = useState(null);
  const [activeTab, setActiveTab] = useState('timeline');
  const [showActorModal, setShowActorModal] = useState(false);
  const [showEvidenceModal, setShowEvidenceModal] = useState(false);
  const [showStageModal, setShowStageModal] = useState(false);
  const [newActor, setNewActor] = useState({ role: '', name: '', function: '', contact: '', stage_id: '' });
  const [newEvidence, setNewEvidence] = useState({ type: '', title: '', description: '', stage_id: '', file: null });
  const [newStage, setNewStage] = useState({ stage_type: '', date: '', location: '', notes: '', decision: '' });

  useEffect(() => {
    if (caseId) {
      loadTrialData();
    }
  }, [caseId]);

  const loadTrialData = async () => {
    try {
      setLoading(true);
      const [caseRes, trialRes] = await Promise.all([
        api.get(`/cases/${caseId}`),
        api.get(`/trials/${caseId}`)
      ]);
      setCaseData(caseRes.data);
      setTrial(trialRes.data);
    } catch (error) {
      console.error('Failed to load trial data:', error);
      // Initialize trial if doesn't exist
      setTrial({ case_id: caseId, stages: [], actors: [], evidence: [], documents: [], history: [] });
    } finally {
      setLoading(false);
    }
  };

  const handleAddStage = async () => {
    try {
      const response = await api.post(`/trials/${caseId}/stages`, newStage);
      toast.success(language === 'fr' ? 'Étape ajoutée' : 'Stage added');
      loadTrialData();
      setShowStageModal(false);
      setNewStage({ stage_type: '', date: '', location: '', notes: '', decision: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    }
  };

  const handleAddActor = async () => {
    try {
      await api.post(`/trials/${caseId}/actors`, newActor);
      toast.success(language === 'fr' ? 'Acteur ajouté' : 'Actor added');
      loadTrialData();
      setShowActorModal(false);
      setNewActor({ role: '', name: '', function: '', contact: '', stage_id: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    }
  };

  const handleAddEvidence = async () => {
    try {
      await api.post(`/trials/${caseId}/evidence`, newEvidence);
      toast.success(language === 'fr' ? 'Élément ajouté' : 'Evidence added');
      loadTrialData();
      setShowEvidenceModal(false);
      setNewEvidence({ type: '', title: '', description: '', stage_id: '', file: null });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    }
  };

  const getStageInfo = (stageType) => TRIAL_STAGES.find(s => s.id === stageType) || { name: { fr: stageType, en: stageType }, icon: Clock };
  const getRoleInfo = (roleId) => ACTOR_ROLES.find(r => r.id === roleId) || { name: { fr: roleId, en: roleId }, icon: User };
  const getEvidenceInfo = (typeId) => EVIDENCE_TYPES.find(e => e.id === typeId) || { name: { fr: typeId, en: typeId }, icon: File };

  const canEdit = ['OPJ', 'JUDICIAL_POLICE_OFFICER', 'MAGISTRATE', 'CLERK', 'SUPER_ADMIN'].includes(user?.role);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="space-y-4">
          <div className="h-8 w-64 skeleton" />
          <div className="h-96 skeleton" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="trial-tracking-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
              <span>{caseData?.case_number}</span>
              <ChevronRight className="w-4 h-4" />
              <span>{language === 'fr' ? 'Suivi du procès' : 'Trial Tracking'}</span>
            </div>
            <h1 className="font-heading text-3xl font-bold tracking-tight flex items-center gap-3">
              <Scale className="w-8 h-8" />
              {caseData?.title || (language === 'fr' ? 'Procès' : 'Trial')}
            </h1>
            <p className="text-muted-foreground mt-1">
              {language === 'fr' ? 'Suivi complet de la procédure judiciaire' : 'Complete judicial procedure tracking'}
            </p>
          </div>
          {canEdit && (
            <div className="flex gap-2">
              <Dialog open={showStageModal} onOpenChange={setShowStageModal}>
                <DialogTrigger asChild>
                  <Button className="gap-2"><Plus className="w-4 h-4" />{language === 'fr' ? 'Nouvelle étape' : 'New Stage'}</Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>{language === 'fr' ? 'Ajouter une étape' : 'Add Stage'}</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label>{language === 'fr' ? 'Type d\'étape' : 'Stage Type'}</Label>
                      <Select value={newStage.stage_type} onValueChange={(v) => setNewStage({...newStage, stage_type: v})}>
                        <SelectTrigger><SelectValue placeholder="Sélectionner..." /></SelectTrigger>
                        <SelectContent>
                          {TRIAL_STAGES.map((s) => (<SelectItem key={s.id} value={s.id}>{s.name[language]}</SelectItem>))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Date</Label>
                        <Input type="datetime-local" value={newStage.date} onChange={(e) => setNewStage({...newStage, date: e.target.value})} />
                      </div>
                      <div className="space-y-2">
                        <Label>{language === 'fr' ? 'Lieu' : 'Location'}</Label>
                        <Input value={newStage.location} onChange={(e) => setNewStage({...newStage, location: e.target.value})} placeholder="Tribunal de..." />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>{language === 'fr' ? 'Notes / Observations' : 'Notes'}</Label>
                      <Textarea value={newStage.notes} onChange={(e) => setNewStage({...newStage, notes: e.target.value})} rows={3} />
                    </div>
                    <div className="space-y-2">
                      <Label>{language === 'fr' ? 'Décision / Résultat' : 'Decision / Result'}</Label>
                      <Input value={newStage.decision} onChange={(e) => setNewStage({...newStage, decision: e.target.value})} />
                    </div>
                    <Button onClick={handleAddStage} className="w-full">{language === 'fr' ? 'Ajouter' : 'Add'}</Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          )}
        </div>

        {/* Case Summary */}
        <Card>
          <CardContent className="p-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">{language === 'fr' ? 'N° Dossier' : 'Case Number'}</p>
                <p className="font-mono font-bold">{caseData?.case_number}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Type</p>
                <p className="font-medium">{caseData?.case_type}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Statut</p>
                <Badge>{caseData?.status}</Badge>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{language === 'fr' ? 'Étape actuelle' : 'Current Stage'}</p>
                <p className="font-medium">{trial?.stages?.length > 0 ? getStageInfo(trial.stages[trial.stages.length - 1]?.stage_type).name[language] : '—'}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList>
            <TabsTrigger value="timeline" className="gap-2"><History className="w-4 h-4" />{language === 'fr' ? 'Chronologie' : 'Timeline'}</TabsTrigger>
            <TabsTrigger value="actors" className="gap-2"><Users className="w-4 h-4" />{language === 'fr' ? 'Acteurs' : 'Actors'}</TabsTrigger>
            <TabsTrigger value="evidence" className="gap-2"><FileText className="w-4 h-4" />{language === 'fr' ? 'Éléments d\'enquête' : 'Evidence'}</TabsTrigger>
            <TabsTrigger value="documents" className="gap-2"><ScrollText className="w-4 h-4" />{language === 'fr' ? 'Documents' : 'Documents'}</TabsTrigger>
          </TabsList>

          {/* Timeline Tab */}
          <TabsContent value="timeline">
            <Card>
              <CardHeader>
                <CardTitle>{language === 'fr' ? 'Chronologie de la procédure' : 'Procedure Timeline'}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="relative">
                  {/* Timeline line */}
                  <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-border" />
                  
                  {/* Stages */}
                  <div className="space-y-6">
                    {TRIAL_STAGES.map((stage, index) => {
                      const stageData = trial?.stages?.find(s => s.stage_type === stage.id);
                      const isCompleted = !!stageData;
                      const isCurrent = trial?.stages?.length > 0 && trial.stages[trial.stages.length - 1]?.stage_type === stage.id;
                      const StageIcon = stage.icon;
                      
                      return (
                        <div key={stage.id} className={`relative flex gap-4 ${!isCompleted && 'opacity-50'}`}>
                          <div className={`w-12 h-12 rounded-full flex items-center justify-center z-10 ${
                            isCompleted ? 'bg-green-100 text-green-600 dark:bg-green-900/30' :
                            isCurrent ? 'bg-blue-100 text-blue-600 dark:bg-blue-900/30' :
                            'bg-muted text-muted-foreground'
                          }`}>
                            {isCompleted ? <CheckCircle className="w-6 h-6" /> : <StageIcon className="w-6 h-6" />}
                          </div>
                          <div className="flex-1 pb-6">
                            <div className="flex items-center justify-between">
                              <h4 className="font-medium">{stage.name[language]}</h4>
                              {stageData && (
                                <span className="text-sm text-muted-foreground">
                                  {new Date(stageData.date).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US')}
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground">{language === 'fr' ? 'Niveau' : 'Level'}: {stage.level}</p>
                            {stageData?.location && (
                              <p className="text-sm flex items-center gap-1 mt-1"><MapPin className="w-3 h-3" />{stageData.location}</p>
                            )}
                            {stageData?.notes && (
                              <p className="text-sm mt-2 p-2 bg-muted rounded">{stageData.notes}</p>
                            )}
                            {stageData?.decision && (
                              <div className="mt-2 p-2 bg-primary/10 rounded border border-primary/20">
                                <p className="text-sm font-medium text-primary">{language === 'fr' ? 'Décision' : 'Decision'}: {stageData.decision}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Actors Tab */}
          <TabsContent value="actors">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>{language === 'fr' ? 'Acteurs du procès' : 'Trial Actors'}</CardTitle>
                {canEdit && (
                  <Dialog open={showActorModal} onOpenChange={setShowActorModal}>
                    <DialogTrigger asChild>
                      <Button size="sm" className="gap-2"><Plus className="w-4 h-4" />{language === 'fr' ? 'Ajouter' : 'Add'}</Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>{language === 'fr' ? 'Ajouter un acteur' : 'Add Actor'}</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4 mt-4">
                        <div className="space-y-2">
                          <Label>{language === 'fr' ? 'Rôle' : 'Role'}</Label>
                          <Select value={newActor.role} onValueChange={(v) => setNewActor({...newActor, role: v})}>
                            <SelectTrigger><SelectValue placeholder="Sélectionner..." /></SelectTrigger>
                            <SelectContent>
                              {ACTOR_ROLES.map((r) => (<SelectItem key={r.id} value={r.id}>{r.name[language]}</SelectItem>))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>{language === 'fr' ? 'Nom complet' : 'Full Name'}</Label>
                          <Input value={newActor.name} onChange={(e) => setNewActor({...newActor, name: e.target.value})} />
                        </div>
                        <div className="space-y-2">
                          <Label>{language === 'fr' ? 'Fonction / Titre' : 'Function / Title'}</Label>
                          <Input value={newActor.function} onChange={(e) => setNewActor({...newActor, function: e.target.value})} placeholder="Ex: Juge au TGI de Kinshasa" />
                        </div>
                        <div className="space-y-2">
                          <Label>{language === 'fr' ? 'Contact' : 'Contact'}</Label>
                          <Input value={newActor.contact} onChange={(e) => setNewActor({...newActor, contact: e.target.value})} />
                        </div>
                        <div className="space-y-2">
                          <Label>{language === 'fr' ? 'Étape d\'intervention' : 'Intervention Stage'}</Label>
                          <Select value={newActor.stage_id} onValueChange={(v) => setNewActor({...newActor, stage_id: v})}>
                            <SelectTrigger><SelectValue placeholder="Sélectionner..." /></SelectTrigger>
                            <SelectContent>
                              {TRIAL_STAGES.map((s) => (<SelectItem key={s.id} value={s.id}>{s.name[language]}</SelectItem>))}
                            </SelectContent>
                          </Select>
                        </div>
                        <Button onClick={handleAddActor} className="w-full">{language === 'fr' ? 'Ajouter' : 'Add'}</Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                )}
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {(trial?.actors || []).map((actor, index) => {
                    const roleInfo = getRoleInfo(actor.role);
                    const RoleIcon = roleInfo.icon;
                    return (
                      <Card key={index} className="bg-muted/50">
                        <CardContent className="p-4">
                          <div className="flex items-start gap-3">
                            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                              <RoleIcon className="w-5 h-5 text-primary" />
                            </div>
                            <div className="flex-1">
                              <p className="font-medium">{actor.name}</p>
                              <p className="text-sm text-primary">{roleInfo.name[language]}</p>
                              {actor.function && <p className="text-xs text-muted-foreground">{actor.function}</p>}
                              {actor.stage_id && (
                                <Badge variant="outline" className="mt-2 text-xs">
                                  {getStageInfo(actor.stage_id).name[language]}
                                </Badge>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                  {(!trial?.actors || trial.actors.length === 0) && (
                    <div className="col-span-full text-center py-8 text-muted-foreground">
                      {language === 'fr' ? 'Aucun acteur enregistré' : 'No actors registered'}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Evidence Tab */}
          <TabsContent value="evidence">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>{language === 'fr' ? "Éléments d'enquête" : 'Investigation Elements'}</CardTitle>
                {canEdit && (
                  <Dialog open={showEvidenceModal} onOpenChange={setShowEvidenceModal}>
                    <DialogTrigger asChild>
                      <Button size="sm" className="gap-2"><Plus className="w-4 h-4" />{language === 'fr' ? 'Ajouter' : 'Add'}</Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>{language === 'fr' ? 'Ajouter un élément' : 'Add Evidence'}</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4 mt-4">
                        <div className="space-y-2">
                          <Label>Type</Label>
                          <Select value={newEvidence.type} onValueChange={(v) => setNewEvidence({...newEvidence, type: v})}>
                            <SelectTrigger><SelectValue placeholder="Sélectionner..." /></SelectTrigger>
                            <SelectContent>
                              {EVIDENCE_TYPES.map((e) => (<SelectItem key={e.id} value={e.id}>{e.name[language]}</SelectItem>))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>{language === 'fr' ? 'Titre / Référence' : 'Title / Reference'}</Label>
                          <Input value={newEvidence.title} onChange={(e) => setNewEvidence({...newEvidence, title: e.target.value})} />
                        </div>
                        <div className="space-y-2">
                          <Label>Description</Label>
                          <Textarea value={newEvidence.description} onChange={(e) => setNewEvidence({...newEvidence, description: e.target.value})} rows={3} />
                        </div>
                        <div className="space-y-2">
                          <Label>{language === 'fr' ? 'Étape de collecte' : 'Collection Stage'}</Label>
                          <Select value={newEvidence.stage_id} onValueChange={(v) => setNewEvidence({...newEvidence, stage_id: v})}>
                            <SelectTrigger><SelectValue placeholder="Sélectionner..." /></SelectTrigger>
                            <SelectContent>
                              {TRIAL_STAGES.map((s) => (<SelectItem key={s.id} value={s.id}>{s.name[language]}</SelectItem>))}
                            </SelectContent>
                          </Select>
                        </div>
                        <Button onClick={handleAddEvidence} className="w-full">{language === 'fr' ? 'Ajouter' : 'Add'}</Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                )}
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {(trial?.evidence || []).map((ev, index) => {
                    const evInfo = getEvidenceInfo(ev.type);
                    const EvIcon = evInfo.icon;
                    return (
                      <div key={index} className="flex items-start gap-4 p-4 border rounded hover:bg-muted/50">
                        <div className="w-10 h-10 rounded bg-muted flex items-center justify-center">
                          <EvIcon className="w-5 h-5 text-muted-foreground" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h4 className="font-medium">{ev.title}</h4>
                            <Badge variant="outline">{evInfo.name[language]}</Badge>
                          </div>
                          <p className="text-sm text-muted-foreground mt-1">{ev.description}</p>
                          {ev.stage_id && (
                            <p className="text-xs text-muted-foreground mt-2">
                              {language === 'fr' ? 'Collecté lors de' : 'Collected during'}: {getStageInfo(ev.stage_id).name[language]}
                            </p>
                          )}
                        </div>
                        <Button variant="ghost" size="icon"><Eye className="w-4 h-4" /></Button>
                      </div>
                    );
                  })}
                  {(!trial?.evidence || trial.evidence.length === 0) && (
                    <div className="text-center py-8 text-muted-foreground">
                      {language === 'fr' ? "Aucun élément d'enquête" : 'No evidence'}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Documents Tab */}
          <TabsContent value="documents">
            <Card>
              <CardHeader>
                <CardTitle>{language === 'fr' ? 'Documents judiciaires' : 'Judicial Documents'}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {JUDICIAL_DOCUMENTS.map((docType) => {
                    const docs = (trial?.documents || []).filter(d => d.type === docType.id);
                    return (
                      <Card key={docType.id} className={`cursor-pointer hover:bg-muted/50 ${docs.length > 0 ? 'border-primary/50' : ''}`}>
                        <CardContent className="p-4 text-center">
                          <FileText className={`w-8 h-8 mx-auto mb-2 ${docs.length > 0 ? 'text-primary' : 'text-muted-foreground'}`} />
                          <p className="font-medium text-sm">{docType.name[language]}</p>
                          <Badge variant={docs.length > 0 ? 'default' : 'secondary'} className="mt-2">
                            {docs.length}
                          </Badge>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

export default TrialTrackingPage;
